import { fetchWithAuth, getCSRFToken } from '../../auth';

const API_HOST = process.env.REACT_APP_ICC_TOOL_API_HOST;

// Always called at request time so the CSRF token is never stale.
const readOpts = () => ({
    headers: { 'X-CSRFToken': getCSRFToken() },
    credentials: 'include',
});

const writeOpts = (method, body) => ({
    method,
    headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCSRFToken() },
    credentials: 'include',
    body: JSON.stringify(body),
});

const deleteOpts = () => ({
    method: 'DELETE',
    headers: { 'X-CSRFToken': getCSRFToken() },
    credentials: 'include',
});

export class FieldValidationError extends Error {
    constructor(fields) {
        super('Validation failed.');
        this.fields = fields;
    }
}

// Rejects if the response is not ok. When `fallback` is provided, always throws a plain
// Error (for endpoints without field-level validation). Otherwise throws FieldValidationError
// so callers can surface individual field messages from the response body.
const throwIfNotOk = async (resp, fallback) => {
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        if (err.detail || fallback) throw new Error(err.detail || fallback);
        throw new FieldValidationError(err);
    }
};

// Builds URLSearchParams from an object, skipping null/undefined/empty values.
const buildQuery = (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    return qs;
};

// Triggers a file download from a Blob response.
function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: filename });
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

export async function getRoadblockTypes({ includeInactive = false } = {}) {
    const url = includeInactive
        ? `${API_HOST}roadblocks/types/?include_inactive=true`
        : `${API_HOST}roadblocks/types/`;
    const resp = await fetchWithAuth(url, readOpts());
    if (!resp.ok) throw new Error('Failed to fetch roadblock types.');
    return resp.json();
}

// Convenience wrapper — fetches all types including inactive (used by the admin manager).
export const getAllRoadblockTypes = () => getRoadblockTypes({ includeInactive: true });

export async function getRoadblocks({ status = 'open', team_ids, start_date, end_date } = {}) {
    const params = buildQuery({ status, start_date, end_date });
    if (team_ids?.length) params.set('team_ids', team_ids.join(','));
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/?${params}`, readOpts());
    if (!resp.ok) throw new Error('Failed to fetch roadblocks.');
    return resp.json();
}

export async function getRoadblockDetail(id) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/${id}/`, readOpts());
    if (!resp.ok) throw new Error('Failed to fetch roadblock details.');
    return resp.json();
}

export async function createRoadblock(payload) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/`, writeOpts('POST', payload));
    await throwIfNotOk(resp);
    return resp.json();
}

export async function updateRoadblock(id, payload) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/${id}/`, writeOpts('PATCH', payload));
    await throwIfNotOk(resp);
    return resp.json();
}

export async function resolveRoadblock(id, { resolution_note, resolution_date, leader_time_spent, leader_time_unit }) {
    return updateRoadblock(id, { status: 'resolved', resolution_note, resolution_date, leader_time_spent, leader_time_unit });
}

export async function addRoadblockNote(id, text) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/${id}/notes/`, writeOpts('POST', { text }));
    if (!resp.ok) throw new Error('Failed to add note.');
    return resp.json();
}

export async function deleteRoadblock(id) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/${id}/`, deleteOpts());
    await throwIfNotOk(resp, 'Failed to delete roadblock.');
    // 204 No Content — no body to parse
}

export async function exportRoadblocks(params = {}) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/export/?${buildQuery(params)}`, readOpts());
    if (!resp.ok) throw new Error('Failed to export roadblocks.');
    downloadBlob(await resp.blob(), 'roadblocks_export.csv');
}

// ─── Dashboard / Report API ───────────────────────────────────────────────────

export async function getRoadblockDashboard(params = {}) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/dashboard/?${buildQuery(params)}`, readOpts());
    if (!resp.ok) throw new Error('Failed to fetch roadblock dashboard.');
    return resp.json();
}

export async function exportRoadblockDashboard(params = {}) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/dashboard/export/?${buildQuery(params)}`, readOpts());
    if (!resp.ok) throw new Error('Failed to export roadblock dashboard.');
    downloadBlob(await resp.blob(), 'roadblocks_report.xlsx');
}

// ─── Type management (ICC Support / superusers only) ─────────────────────────

export async function createRoadblockType(payload) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/types/`, writeOpts('POST', payload));
    await throwIfNotOk(resp);
    return resp.json();
}

export async function updateRoadblockType(id, payload) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/types/${id}/`, writeOpts('PATCH', payload));
    await throwIfNotOk(resp);
    return resp.json();
}

// ─── Subtype management ───────────────────────────────────────────────────────

export async function createRoadblockSubtype(typeId, payload) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/types/${typeId}/subtypes/`, writeOpts('POST', payload));
    await throwIfNotOk(resp);
    return resp.json();
}

export async function updateRoadblockSubtype(subtypeId, payload) {
    const resp = await fetchWithAuth(`${API_HOST}roadblocks/subtypes/${subtypeId}/`, writeOpts('PATCH', payload));
    await throwIfNotOk(resp);
    return resp.json();
}
